// LLM generated - Model Claude 4 Sonnet
import { useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';
import dt from '~/domains/contest/domains/gamecenter/testing.const';

import { TeamPickIconDisplay } from './TeamPickIconDisplay';
import { useTeamSurvivorEntryDisplayStyles } from './TeamSurvivorEntryDisplay.styles';

export type Pick = {
  id: string;
  name: string;
  image: string;
  spread?: string;
};

export type Slate = {
  id: string;
  name: string;
  picks: (Pick | null)[];
  isMakePicks?: boolean;
};

export type TeamSurvivorEntryDisplayProps = {
  contestId: string;
  entryId: string;
  slate: Slate;
  slateIndex: number;
  isSlateMissingPicks: boolean;
  requiredPicksCount: number;
  isLinkEnabled?: boolean;
  linkHref?: string;
  linkReplace?: boolean;
  linkShallow?: boolean;
  linkScroll?: boolean;
  className?: string;
  winLossClass?: string;
  canMakePicks?: boolean;
  hasHiddenPicks?: boolean;
};

export function TeamSurvivorEntryDisplay({
  contestId,
  entryId,
  slate,
  slateIndex,
  isSlateMissingPicks,
  requiredPicksCount,
  isLinkEnabled = true,
  linkHref,
  linkReplace = false,
  linkShallow = false,
  linkScroll = true,
  className,
  winLossClass,
  canMakePicks = true,
  hasHiddenPicks = false,
}: TeamSurvivorEntryDisplayProps) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();

  // Calculate width: [sidePadding][pick][gap][pick][gap][pick][sidePadding]
  // Use requiredPicksCount for empty slates to ensure proper width for expected picks
  const effectivePicksCount = slate.picks.length > 0 ? slate.picks.length : requiredPicksCount;
  const width =
    // [pick][gap]
    effectivePicksCount * (48 + theme.other.spacing._8) -
    // remove last gap that is not needed
    theme.other.spacing._8 +
    // [sidePadding]
    theme.other.spacing._16 * 2 +
    2;

  const { classes, cx } = useTeamSurvivorEntryDisplayStyles({ width });

  // Only the pick controller can act on a missing pick, so the orange nudge is theirs alone.
  const showsMissingPicksNudge = isSlateMissingPicks && Boolean(slate.isMakePicks) && canMakePicks;

  // Default link href if not provided
  const defaultLinkHref =
    linkHref || Routes.gamecenterPicks(contestId, { entryId, slateId: slate.id });

  const content = (
    <>
      <Text variant="body-small" className={classes.slateLabel} title={slate.name}>
        {slate.name}
      </Text>
      <div className={cx(classes.slatePicks, winLossClass)}>
        <TeamPickIconDisplay
          picks={slate.picks}
          isSlateMissingPicks={isSlateMissingPicks}
          isMakePicks={slate.isMakePicks}
          requiredPicksCount={requiredPicksCount}
          canMakePicks={canMakePicks}
          hasHiddenPicks={hasHiddenPicks}
        />
      </div>
    </>
  );

  if (!isLinkEnabled) {
    return (
      <div
        className={cx(
          classes.slate,
          {
            isSlateMissingPicks: showsMissingPicksNudge,
          },
          className
        )}
        data-test-id={dt.common.entriesScheduled.entry.slate}
      >
        {content}
      </div>
    );
  }

  return (
    <UnstyledNextLink
      className={cx(
        classes.slate,
        'isInteractive',
        {
          isSlateMissingPicks: showsMissingPicksNudge,
        },
        className
      )}
      href={defaultLinkHref}
      replace={linkReplace}
      shallow={linkShallow}
      scroll={linkScroll}
      data-test-id={dt.common.entriesScheduled.entry.slate}
    >
      {content}
    </UnstyledNextLink>
  );
}
